import { useEffect, useRef } from "react";

/**
 * Close a popover when the user clicks outside it or presses Escape.
 *
 * `ref` is the element that should NOT dismiss — clicks inside it are ignored.
 * Listeners exist only while `open`, so a closed popover costs nothing and
 * cannot respond to stray events.
 *
 * `onClose` is held in a ref rather than listed as a dependency. Callers pass
 * an inline arrow, so a dependency on it would tear down and re-register both
 * document listeners on every render — churn for no benefit. The ref keeps the
 * latest callback without re-running the effect.
 */
export default function useDismissOnOutside(ref, open, onClose) {
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  });

  useEffect(() => {
    if (!open) return undefined;

    const onDocClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onCloseRef.current();
    };
    const onKey = (e) => {
      if (e.key === "Escape") onCloseRef.current();
    };

    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [ref, open]);
}
